import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Shield, Users, Calendar, FileText, Clock, Award, 
  CheckCircle, TrendingUp, Zap, Heart, Star, 
  ArrowRight, Lock, Smartphone, 
  Check, DollarSign, Target,
  Twitter, Linkedin, Globe,
  Brain, AlertTriangle, Gift, CreditCard, LineChart, UserCheck,
  Crown, XCircle, Eye, ChevronDown
} from 'lucide-react';
import Logo from '../components/Logo';
import VideoModal from '../components/VideoModal';
import DemoSchedulingModal from '../components/DemoSchedulingModal';
import TrustedBySlider from '../components/TrustedBySlider';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ModernLandingPage = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [countersVisible, setCountersVisible] = useState(false);
  const [mrrCount, setMrrCount] = useState(0);
  const [churnCount, setChurnCount] = useState(0);
  const [revenueCount, setRevenueCount] = useState(0);
  const [trustedByContent, setTrustedByContent] = useState(null);
  const [dashboardPreview, setDashboardPreview] = useState({
    revenue_badge: '+40%',
    active_members_count: '2,847',
    active_members_growth: '+23%',
    churn_accuracy: '92%',
    revenue_growth: '+40%'
  });
  const [footerContent, setFooterContent] = useState({
    company_name: 'Clinicax Systems',
    twitter_url: '',
    linkedin_url: '',
    facebook_url: ''
  });

  // Static data
  const marketProblems = [
    'Pricing based on intuition, not data',
    'Silent member churn without warning',
    'No insight into service value',
    'Manual quota & billing management',
    'Zero revenue visibility'
  ];

  const tangibleBenefits = [
    { icon: TrendingUp, title: 'Grow MRR', desc: 'Optimize pricing automatically' },
    { icon: UserCheck, title: 'Reduce Churn', desc: 'Predict & prevent cancellations' },
    { icon: Target, title: 'Track Usage', desc: 'Real-time quota monitoring' },
    { icon: Award, title: 'Better Plans', desc: 'Data-driven plan design' },
    { icon: Clock, title: 'Save Time', desc: 'Automate everything' },
    { icon: Brain, title: 'Smart Decisions', desc: 'AI-powered insights' }
  ];

  const testimonials = [
    {
      quote: "The AI pricing suggestions increased our MRR by 23% in Q1.",
      author: "Dr. Sarah Chen",
      role: "Medical Director",
      clinic: "Wellness Family Care"
    },
    {
      quote: "Churn prediction saved us from losing high-value members.",
      author: "Dr. Michael Rodriguez",
      role: "Practice Owner",
      clinic: "Rodriguez Medical Group"
    },
    {
      quote: "Finally, a platform that understands membership healthcare.",
      author: "Dr. Emily Watson",
      role: "Practice Manager",
      clinic: "Holistic Health Partners"
    }
  ];

  useEffect(() => {
    // Use timeout to avoid setState in effect synchronously
    const visibilityTimer = setTimeout(() => {
      setIsVisible(true);
      setCountersVisible(true);
    }, 10);
    
    // Animate counters
    const animateCounter = (setter, target, duration = 2000) => {
      let start = 0;
      const increment = target / (duration / 16);
      const timer = setInterval(() => {
        start += increment;
        if (start >= target) {
          setter(target);
          clearInterval(timer);
        } else {
          setter(Math.floor(start));
        }
      }, 16);
      return timer;
    };

    const timer1 = animateCounter(setMrrCount, 23);
    const timer2 = animateCounter(setChurnCount, 92);
    const timer3 = animateCounter(setRevenueCount, 40);
    
    // Load pricing
    const loadPricing = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/subscription-tiers/public`);
        if (response.data && Array.isArray(response.data)) {
          setPricingTiers(response.data.sort((a, b) => a.monthly_price - b.monthly_price));
        }
      } catch (error) {
        console.error('Error loading pricing tiers:', error);
      }
    };
    loadPricing();

    // Load CMS content (trusted_by section and dashboard_preview)
    const loadCMSContent = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/homepage-cms`);
        if (response.data?.trusted_by) {
          setTrustedByContent(response.data.trusted_by);
        } else if (response.data?.content?.trusted_by) {
          setTrustedByContent(response.data.content.trusted_by);
        }
        
        // Load footer content (social links)
        if (response.data?.footer) {
          setFooterContent(prev => ({ ...prev, ...response.data.footer }));
        }
        
        // Load dashboard preview values
        if (response.data?.dashboard_preview) {
          const dp = response.data.dashboard_preview;
          setDashboardPreview(dp);
          
          // Parse CMS values for counters (remove % and + signs)
          const parsePercent = (str) => parseInt(str?.replace(/[^0-9]/g, '') || '0');
          
          // Re-animate counters with CMS values
          const cmsGrowth = parsePercent(dp.active_members_growth) || 23;
          const cmsChurn = parsePercent(dp.churn_accuracy) || 92;
          const cmsRevenue = parsePercent(dp.revenue_growth) || 40;
          
          animateCounter(setMrrCount, cmsGrowth);
          animateCounter(setChurnCount, cmsChurn);
          animateCounter(setRevenueCount, cmsRevenue);
        }
      } catch (error) {
        console.error('Error loading CMS content:', error);
      }
    };
    loadCMSContent();

    // Testimonial rotation
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % 3);
    }, 5000);

    // Mouse parallax effect
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20
      });
    };
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      clearTimeout(visibilityTimer);
      clearInterval(interval);
      clearInterval(timer1);
      clearInterval(timer2);
      clearInterval(timer3);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const getPricingPlans = () => {
    if (pricingTiers.length > 0) {
      return pricingTiers.map(tier => {
        const features = [];
        
        // Core limits first (these are the most important)
        if (tier.max_members > 0) features.push(`Up to ${tier.max_members.toLocaleString()} patients`);
        else if (tier.max_members === -1) features.push('Unlimited patients');
        if (tier.max_users > 0) features.push(`${tier.max_users} staff users`);
        else if (tier.max_users === -1) features.push('Unlimited staff users');
        if (tier.max_storage_gb > 0) features.push(`${tier.max_storage_gb}GB storage`);
        
        // Feature mapping with categories
        const featureLabels = {
          // Core features
          basic_membership_management: 'Membership management',
          patient_portal: 'Patient portal',
          appointment_scheduling: 'Appointment scheduling',
          payment_processing: 'Payment processing',
          email_notifications: 'Email notifications',
          basic_reporting: 'Basic reporting',
          automated_reminders: 'Automated reminders',
          secure_messaging: 'Secure messaging',
          multiple_membership_tiers: 'Multiple membership tiers',
          custom_branding: 'Custom branding',
          // AI features
          ai_membership_builder: 'AI Membership Builder',
          ai_pricing_insights: 'AI Pricing Insights',
          ai_churn_prediction: 'AI Churn Prediction',
          ai_analytics_dashboard: 'AI Analytics Dashboard',
          advanced_analytics: 'Advanced analytics',
          revenue_projections: 'Revenue projections',
          // Premium features
          stripe_connect: 'Stripe Connect',
          api_access: 'API access',
          priority_support: 'Priority support',
          dedicated_manager: 'Dedicated account manager',
          hipaa_compliance: 'HIPAA compliance tools',
          white_label: 'White-label options',
          custom_integrations: 'Custom integrations',
          sla_guarantee: 'SLA guarantee'
        };
        
        if (tier.features) {
          Object.entries(tier.features).forEach(([key, value]) => {
            if (value && featureLabels[key]) {
              features.push(featureLabels[key]);
            }
          });
        }
        
        return {
          name: tier.name,
          price: billingCycle === 'yearly' ? tier.annual_price : tier.monthly_price,
          period: '/mo',
          billedAs: billingCycle === 'yearly' ? `$${(tier.annual_price * 12).toFixed(0)}/year` : 'Billed monthly',
          description: tier.description,
          features: features,
          popular: tier.is_popular,
          transactionFee: tier.transaction_fee_percentage,
          maxPatients: tier.max_members,
          maxStaff: tier.max_users,
          storage: tier.max_storage_gb
        };
      });
    }
    
    // Fallback plans with comprehensive features
    return [
      { 
        name: 'Starter', 
        price: billingCycle === 'yearly' ? 159 : 199, 
        period: '/mo',
        billedAs: billingCycle === 'yearly' ? '$1,908/year' : 'Billed monthly',
        description: 'Perfect for solo practitioners',
        features: [
          'Up to 300 patients',
          '2 staff users', 
          '1GB storage',
          'Membership management', 
          'Patient portal', 
          'Appointment scheduling', 
          'Payment processing', 
          'Email notifications', 
          'Basic reporting',
          'Multiple membership tiers'
        ],
        popular: false,
        transactionFee: 2.0,
        maxPatients: 300,
        maxStaff: 2,
        storage: 1
      },
      { 
        name: 'Professional', 
        price: billingCycle === 'yearly' ? 319 : 399, 
        period: '/mo',
        billedAs: billingCycle === 'yearly' ? '$3,828/year' : 'Billed monthly',
        description: 'For growing practices',
        features: [
          'Up to 1,500 patients',
          '8 staff users',
          '10GB storage',
          'Membership management',
          'Patient portal',
          'Appointment scheduling',
          'Payment processing',
          'Email notifications',
          'Basic reporting',
          'Automated reminders',
          'Secure messaging',
          'Multiple membership tiers',
          'Custom branding',
          'AI Analytics Dashboard',
          'AI Churn Prediction',
          'Revenue projections',
          'Priority support'
        ],
        popular: true,
        transactionFee: 1.5,
        maxPatients: 1500,
        maxStaff: 8,
        storage: 10
      },
      { 
        name: 'Growth', 
        price: billingCycle === 'yearly' ? 559 : 699, 
        period: '/mo',
        billedAs: billingCycle === 'yearly' ? '$6,708/year' : 'Billed monthly',
        description: 'For large organizations',
        features: [
          'Up to 5,000 patients',
          'Unlimited staff users',
          '50GB storage',
          'Membership management',
          'Patient portal',
          'Appointment scheduling',
          'Payment processing',
          'Email notifications',
          'Basic reporting',
          'Automated reminders',
          'Secure messaging',
          'Multiple membership tiers',
          'Custom branding',
          'AI Analytics Dashboard',
          'AI Churn Prediction',
          'AI Pricing Insights',
          'AI Membership Builder',
          'Advanced analytics',
          'Revenue projections',
          'Stripe Connect',
          'API access',
          'Priority support',
          'Dedicated account manager',
          'HIPAA compliance tools'
        ],
        popular: false,
        transactionFee: 1.0,
        maxPatients: 5000,
        maxStaff: -1,
        storage: 50
      }
    ];
  };

  const pricingPlans = getPricingPlans();
  const [expandedPlans, setExpandedPlans] = useState({});

  const togglePlanExpand = (planName) => {
    setExpandedPlans(prev => ({
      ...prev,
      [planName]: !prev[planName]
    }));
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Custom Styles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotateX(10deg) rotateY(-10deg); }
          50% { transform: translateY(-20px) rotateX(10deg) rotateY(-10deg); }
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(20, 184, 166, 0.3); }
          50% { box-shadow: 0 0 40px rgba(20, 184, 166, 0.6); }
        }
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-in-left {
          from { opacity: 0; transform: translateX(-60px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slide-in-right {
          from { opacity: 0; transform: translateX(60px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.8); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes bounce-soft {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-pulse-glow { animation: pulse-glow 3s ease-in-out infinite; }
        .animate-gradient { 
          background-size: 200% 200%;
          animation: gradient-shift 8s ease infinite;
        }
        .animate-slide-up { animation: slide-up 0.8s ease-out forwards; }
        .animate-slide-left { animation: slide-in-left 0.8s ease-out forwards; }
        .animate-slide-right { animation: slide-in-right 0.8s ease-out forwards; }
        .animate-scale-in { animation: scale-in 0.6s ease-out forwards; }
        .animate-bounce-soft { animation: bounce-soft 2s ease-in-out infinite; }
        .delay-100 { animation-delay: 0.1s; }
        .delay-200 { animation-delay: 0.2s; }
        .delay-300 { animation-delay: 0.3s; }
        .delay-400 { animation-delay: 0.4s; }
        .delay-500 { animation-delay: 0.5s; }
        .glass {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }
        .glass-dark {
          background: rgba(0, 0, 0, 0.2);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        .text-gradient {
          background: linear-gradient(135deg, #017ec6, #0056b3, #3b82f6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .phone-3d {
          transform-style: preserve-3d;
          perspective: 1000px;
        }
        .phone-inner {
          transform: rotateX(5deg) rotateY(-15deg);
          transition: transform 0.5s ease;
        }
        .phone-inner:hover {
          transform: rotateX(0deg) rotateY(0deg) scale(1.05);
        }
        .card-hover {
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        .card-hover:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
      `}</style>

      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-xl z-50 border-b border-gray-100/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <Logo />
              <div className="flex flex-col">
                <span className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent leading-tight">MyMedMember</span>
                <span className="text-[10px] text-gray-500 font-medium -mt-1">by Clinicax Systems</span>
              </div>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#solution" className="text-gray-600 hover:text-blue-600 transition-all font-medium hover:scale-105">Solution</a>
              <a href="#ai-analytics" className="text-gray-600 hover:text-blue-600 transition-all font-medium hover:scale-105">AI Analytics</a>
              <a href="#pricing" className="text-gray-600 hover:text-blue-600 transition-all font-medium hover:scale-105">Pricing</a>
            </div>

            <div className="flex items-center gap-3">
              <Link to="/login" className="hidden sm:block">
                <Button variant="ghost" className="text-gray-600 hover:text-blue-600 hover:scale-105 transition-all">
                  Sign In
                </Button>
              </Link>
              <Button 
                className="bg-gradient-to-r from-blue-600 via-blue-500 to-blue-400 hover:from-blue-700 hover:via-blue-600 hover:to-blue-500 text-white shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 transition-all hover:scale-105 animate-gradient"
                onClick={() => setIsDemoModalOpen(true)}
              >
                Get Demo
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* ========== HERO SECTION ========== */}
      <section className="relative min-h-screen pt-24 pb-16 overflow-hidden flex items-center">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50/50 to-sky-50/50" />
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            background: `radial-gradient(circle at ${50 + mousePosition.x}% ${50 + mousePosition.y}%, rgba(20, 184, 166, 0.15) 0%, transparent 50%)`
          }}
        />
        
        {/* Floating Orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-blue-400/20 to-sky-400/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-blue-300/10 to-sky-300/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className={`${isVisible ? 'animate-slide-left' : 'opacity-0'}`}>
              <Badge className="mb-6 bg-gradient-to-r from-blue-500/10 to-sky-500/10 text-blue-700 border border-blue-200 px-4 py-2 text-sm font-medium hover:scale-105 transition-transform cursor-default">
                <Brain className="w-4 h-4 mr-2" />
                AI-Powered Platform
              </Badge>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1] tracking-tight">
                <span className="text-gray-900">Grow Revenue.</span>
                <br />
                <span className="text-gradient">Retain Members.</span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed max-w-xl">
                AI-powered membership management that optimizes pricing, predicts churn, and automates your clinic.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-blue-600 via-blue-500 to-blue-400 hover:from-blue-700 hover:via-blue-600 hover:to-blue-500 text-white text-lg px-8 py-7 shadow-2xl shadow-blue-500/30 hover:shadow-blue-500/50 transition-all hover:scale-105 animate-gradient animate-pulse-glow"
                  onClick={() => setIsDemoModalOpen(true)}
                >
                  Request Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 text-lg px-8 py-7 w-full transition-all hover:scale-105"
                  onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  View Pricing
                </Button>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-4">
                {['30-day free trial', 'HIPAA-ready', 'No credit card'].map((badge, i) => (
                  <div key={i} className={`flex items-center gap-2 text-gray-500 text-sm ${isVisible ? 'animate-slide-up' : 'opacity-0'}`} style={{ animationDelay: `${0.5 + i * 0.1}s` }}>
                    <CheckCircle className="h-4 w-4 text-blue-600" />
                    <span>{badge}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Content - Stats Cards */}
            <div className={`relative ${isVisible ? 'animate-slide-right' : 'opacity-0'}`}>
              <div className="relative">
                {/* Main Dashboard Preview */}
                <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-gray-900 to-gray-800 p-1">
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-semibold text-gray-900">AI Analytics Dashboard</h3>
                      <Badge className="bg-green-100 text-green-700">Live</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-blue-500 to-sky-500 rounded-xl p-4 text-white">
                        <p className="text-3xl font-bold">+{mrrCount}%</p>
                        <p className="text-sm opacity-90">MRR Growth</p>
                      </div>
                      <div className="bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl p-4 text-white">
                        <p className="text-3xl font-bold">{churnCount}%</p>
                        <p className="text-sm opacity-90">Churn Accuracy</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating Cards */}
                <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl p-4 border border-gray-100 animate-bounce-soft">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Revenue</p>
                      <p className="font-bold text-green-600">+{revenueCount}%</p>
                    </div>
                  </div>
                </div>

                <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl p-4 border border-gray-100 animate-bounce-soft" style={{ animationDelay: '0.5s' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-sky-500 rounded-full flex items-center justify-center">
                      <Users className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Active Members</p>
                      <p className="font-bold text-blue-600">{dashboardPreview.active_members_count}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <ChevronDown className="h-8 w-8 text-gray-400" />
          </div>
        </div>
      </section>

      {/* ========== TRUSTED BY SECTION ========== */}
      {trustedByContent?.providers && trustedByContent.providers.length > 0 && (
        <TrustedBySlider 
          providers={trustedByContent.providers}
          title={trustedByContent.title}
          subtitle={trustedByContent.subtitle}
        />
      )}

      {/* ========== PROBLEM SECTION ========== */}
      <section id="problem" className="py-24 bg-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOCAxOC04LjA1OSAxOC0xOC04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjAyIi8+PC9nPjwvc3ZnPg==')] opacity-20" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className={`text-center mb-16 ${isVisible ? 'animate-slide-up' : 'opacity-0'}`}>
            <Badge className="mb-4 bg-red-500/20 text-red-300 border border-red-500/30 px-4 py-2">
              <AlertTriangle className="w-4 h-4 mr-2" />
              The Challenge
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Membership clinics <span className="text-red-400">struggle</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
            {marketProblems.map((problem, index) => (
              <div 
                key={index} 
                className={`flex items-center gap-4 p-5 glass-dark rounded-2xl hover:bg-white/10 transition-all cursor-default card-hover ${isVisible ? 'animate-scale-in' : 'opacity-0'}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="bg-red-500/20 p-2 rounded-lg">
                  <XCircle className="h-5 w-5 text-red-400" />
                </div>
                <p className="text-gray-300 text-sm">{problem}</p>
              </div>
            ))}
          </div>

          <p className={`text-center text-xl font-medium ${isVisible ? 'animate-slide-up delay-500' : 'opacity-0'}`}>
            <span className="text-gradient">MyMedMember by Clinicax Systems</span> solves all of this.
          </p>
        </div>
      </section>

      {/* ========== SOLUTION SECTION ========== */}
      <section id="solution" className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className={isVisible ? 'animate-slide-left' : 'opacity-0'}>
              <Badge className="mb-4 bg-blue-100 text-blue-700 border-0 px-4 py-2">
                <Zap className="w-4 h-4 mr-2" />
                The Solution
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-gradient">AI-powered</span> membership management
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Transform data into decisions. Automate operations. Grow revenue.
              </p>

              <div className="space-y-4">
                {['Create custom membership plans', 'Automate payments & quotas', 'Monitor real-time metrics', 'Make data-driven decisions'].map((item, i) => (
                  <div 
                    key={i} 
                    className={`flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-sky-50 rounded-xl hover:from-blue-100 hover:to-sky-100 transition-all cursor-default card-hover ${isVisible ? 'animate-slide-up' : 'opacity-0'}`}
                    style={{ animationDelay: `${0.2 + i * 0.1}s` }}
                  >
                    <CheckCircle className="h-6 w-6 text-blue-600 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className={`relative ${isVisible ? 'animate-slide-right' : 'opacity-0'}`}>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1551434678-e076c223a692?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Dashboard"
                  className="rounded-3xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -right-6 bg-gradient-to-r from-blue-500 to-sky-500 p-6 rounded-2xl shadow-xl text-white animate-bounce-soft">
                  <p className="text-sm opacity-90">Average growth</p>
                  <p className="text-4xl font-bold">+40%</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== AI ANALYTICS SECTION ========== */}
      <section id="ai-analytics" className="py-24 bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 ${isVisible ? 'animate-slide-up' : 'opacity-0'}`}>
            <Badge className="mb-4 bg-white/10 text-white border border-white/20 px-4 py-2 backdrop-blur-sm">
              <Brain className="w-4 h-4 mr-2" />
              Core Differentiator
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
              AI that <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-sky-400 bg-clip-text text-transparent">drives growth</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: DollarSign, title: 'Dynamic Pricing', desc: 'Optimize prices based on data', color: 'from-green-400 to-emerald-500', items: ['Adoption rates', 'MRR tracking', 'Market position'] },
              { icon: AlertTriangle, title: 'Churn Prediction', desc: 'Identify at-risk members', color: 'from-orange-400 to-red-500', items: ['Risk scores', 'Payment history', 'Engagement patterns'] },
              { icon: LineChart, title: 'Feature Analysis', desc: 'Know what members value', color: 'from-blue-400 to-sky-500', items: ['Usage ranking', 'Plan distribution', 'Service preferences'] }
            ].map((card, index) => (
              <Card 
                key={index} 
                className={`glass border-white/10 hover:bg-white/20 transition-all duration-500 card-hover ${isVisible ? 'animate-scale-in' : 'opacity-0'}`}
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <CardHeader>
                  <div className={`mb-4 w-14 h-14 rounded-2xl bg-gradient-to-br ${card.color} p-3 shadow-lg`}>
                    <card.icon className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-xl text-white">{card.title}</CardTitle>
                  <CardDescription className="text-purple-200">{card.desc}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {card.items.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-purple-100 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-400" />
                      <span>{item}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ========== BENEFITS SECTION ========== */}
      <section id="benefits" className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 ${isVisible ? 'animate-slide-up' : 'opacity-0'}`}>
            <Badge className="mb-4 bg-green-100 text-green-700 border-0 px-4 py-2">
              <TrendingUp className="w-4 h-4 mr-2" />
              Results
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold">
              Real <span className="text-gradient">business impact</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tangibleBenefits.map((benefit, index) => (
              <div 
                key={index} 
                className={`group p-6 bg-white rounded-2xl border border-gray-100 hover:border-blue-200 card-hover ${isVisible ? 'animate-scale-in' : 'opacity-0'}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="mb-4 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-sky-500 p-3 group-hover:scale-110 transition-transform">
                  <benefit.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== PATIENT PORTAL - 3D PHONE ========== */}
      <section id="patient-portal" className="py-24 bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className={isVisible ? 'animate-slide-left' : 'opacity-0'}>
              <Badge className="mb-4 bg-white/10 text-white border border-white/20 px-4 py-2">
                <Smartphone className="w-4 h-4 mr-2" />
                Patient Experience
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                A portal they&apos;ll <span className="bg-gradient-to-r from-sky-400 to-blue-400 bg-clip-text text-transparent">love</span>
              </h2>
              <p className="text-lg text-blue-200 mb-8">
                Mobile-first design for the modern patient.
              </p>

              <div className="space-y-4">
                {[
                  { icon: Eye, text: 'View membership & benefits' },
                  { icon: Target, text: 'Track remaining quotas' },
                  { icon: Calendar, text: 'Book appointments easily' },
                  { icon: CreditCard, text: 'Digital membership card' },
                  { icon: DollarSign, text: 'Manage payments' }
                ].map((item, i) => (
                  <div 
                    key={i} 
                    className={`flex items-center gap-4 p-4 glass rounded-xl hover:bg-white/20 transition-all ${isVisible ? 'animate-slide-up' : 'opacity-0'}`}
                    style={{ animationDelay: `${0.2 + i * 0.1}s` }}
                  >
                    <item.icon className="h-5 w-5 text-sky-400" />
                    <span className="text-white">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 3D Phone Mockup */}
            <div className={`phone-3d flex justify-center ${isVisible ? 'animate-slide-right' : 'opacity-0'}`}>
              <div className="phone-inner relative animate-float">
                {/* Phone Frame */}
                <div className="relative w-[280px] h-[580px] bg-gradient-to-b from-gray-800 to-gray-900 rounded-[3rem] p-2 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5),0_30px_60px_-30px_rgba(0,0,0,0.5),inset_0_-4px_8px_rgba(0,0,0,0.2)]">
                  {/* Phone Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-gray-900 rounded-b-2xl z-20" />
                  
                  {/* Phone Screen */}
                  <div className="relative w-full h-full bg-white rounded-[2.5rem] overflow-hidden">
                    {/* Status Bar */}
                    <div className="flex justify-between items-center px-6 pt-8 pb-2 bg-gradient-to-r from-blue-500 to-sky-500">
                      <span className="text-white text-xs font-medium">9:41</span>
                      <div className="flex gap-1">
                        <div className="w-4 h-2 bg-white/80 rounded-sm" />
                        <div className="w-4 h-2 bg-white/60 rounded-sm" />
                        <div className="w-4 h-2 bg-white/40 rounded-sm" />
                      </div>
                    </div>

                    {/* App Header */}
                    <div className="bg-gradient-to-r from-blue-500 to-sky-500 px-5 pb-6 pt-2">
                      <p className="text-white/80 text-sm">Welcome back,</p>
                      <p className="text-white text-xl font-bold">Sarah Johnson</p>
                    </div>

                    {/* App Content */}
                    <div className="p-5 space-y-4 bg-gray-50">
                      {/* Membership Card */}
                      <div className="bg-gradient-to-br from-blue-500 to-sky-600 rounded-2xl p-4 text-white shadow-lg">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="text-xs opacity-80">Essential Care Plan</p>
                            <p className="text-lg font-bold">Premium Member</p>
                          </div>
                          <Badge className="bg-white/20 text-white text-xs border-0">Active</Badge>
                        </div>
                        <div className="flex justify-between items-end">
                          <div>
                            <p className="text-3xl font-bold">3<span className="text-lg opacity-80">/5</span></p>
                            <p className="text-xs opacity-80">Consultations left</p>
                          </div>
                          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                            <Crown className="w-6 h-6" />
                          </div>
                        </div>
                      </div>

                      {/* Quick Actions */}
                      <div className="grid grid-cols-2 gap-3">
                        <button className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow text-center">
                          <Calendar className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                          <p className="text-xs text-gray-600 font-medium">Book Appt</p>
                        </button>
                        <button className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow text-center">
                          <CreditCard className="h-6 w-6 text-blue-500 mx-auto mb-2" />
                          <p className="text-xs text-gray-600 font-medium">My Card</p>
                        </button>
                      </div>

                      {/* Notification */}
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                            <Gift className="h-5 w-5 text-amber-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-amber-900">New benefit unlocked!</p>
                            <p className="text-xs text-amber-600">10% pharmacy discount</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating Elements */}
                <div className="absolute -top-8 -right-8 bg-white rounded-2xl shadow-2xl p-3 animate-bounce-soft">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-sm font-medium text-gray-700">Mobile-First</span>
                  </div>
                </div>
                <div className="absolute -bottom-8 -left-8 bg-white rounded-2xl shadow-2xl p-3 animate-bounce-soft" style={{ animationDelay: '0.5s' }}>
                  <div className="flex items-center gap-2">
                    <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                    <span className="text-sm font-medium text-gray-700">4.9 Rating</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECURITY SECTION ========== */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              Enterprise-grade <span className="text-green-400">security</span>
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-8">
            {[
              { icon: Lock, text: 'Role-based access' },
              { icon: Shield, text: 'JWT authentication' },
              { icon: FileText, text: 'Audit logs' },
              { icon: Heart, text: 'HIPAA-ready' },
              { icon: CreditCard, text: 'PCI compliant' }
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 text-gray-300">
                <item.icon className="h-5 w-5 text-green-400" />
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIALS ========== */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-6 w-6 text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <p className="text-2xl md:text-3xl text-gray-700 mb-8 italic">
              &ldquo;{testimonials[activeTestimonial]?.quote}&rdquo;
            </p>
            <p className="font-semibold text-gray-900">{testimonials[activeTestimonial]?.author}</p>
            <p className="text-gray-500">{testimonials[activeTestimonial]?.role}, {testimonials[activeTestimonial]?.clinic}</p>
            
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === activeTestimonial ? 'bg-blue-500 w-8' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== PRICING ========== */}
      <section id="pricing" className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-700 border-0 px-4 py-2">
              <DollarSign className="w-4 h-4 mr-2" />
              Pricing
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Simple, transparent <span className="text-gradient">pricing</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              Choose the plan that fits your practice. All plans include a 30-day free trial.
            </p>
            
            {/* Billing Toggle */}
            <div className="inline-flex items-center justify-center gap-4 bg-gray-100 rounded-full px-2 py-2">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 rounded-full text-base font-medium transition-all ${
                  billingCycle === 'monthly' 
                    ? 'bg-white text-gray-900 shadow-md' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-6 py-2 rounded-full text-base font-medium transition-all flex items-center gap-2 ${
                  billingCycle === 'yearly' 
                    ? 'bg-white text-gray-900 shadow-md' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Yearly
                <span className={`text-xs px-2 py-0.5 rounded-full transition-all ${
                  billingCycle === 'yearly' 
                    ? 'bg-green-500 text-white' 
                    : 'bg-green-100 text-green-700'
                }`}>
                  -20%
                </span>
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 items-start">
            {pricingPlans.map((plan, index) => {
              const visibleFeatures = 4;
              const totalFeatures = plan.features.length;
              const hiddenFeatures = totalFeatures - visibleFeatures;
              const isExpanded = expandedPlans[plan.name];
              const displayedFeatures = isExpanded ? plan.features : plan.features.slice(0, visibleFeatures);
              
              return (
                <Card 
                  key={index} 
                  className={`relative card-hover overflow-hidden ${
                    plan.popular 
                      ? 'border-2 border-blue-500 shadow-2xl shadow-blue-500/20 scale-105 z-10' 
                      : 'border-2 border-gray-100 hover:border-blue-200'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-0 left-0 right-0">
                      <div className="bg-gradient-to-r from-blue-500 to-sky-500 text-white text-center py-2 text-sm font-semibold">
                        Most Popular
                      </div>
                    </div>
                  )}
                  
                  <CardHeader className={`text-center ${plan.popular ? 'pt-12' : 'pt-8'} pb-4`}>
                    <CardTitle className="text-2xl font-bold text-gray-900">{plan.name}</CardTitle>
                    <CardDescription className="text-gray-600 mt-2">{plan.description}</CardDescription>
                    
                    <div className="mt-6">
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-5xl font-bold text-gray-900">${plan.price}</span>
                        <span className="text-gray-500 text-lg">{plan.period}</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-2">{plan.billedAs}</p>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-0 pb-8">
                    {/* Key Metrics */}
                    <div className="grid grid-cols-3 gap-2 mb-6 p-3 bg-gray-50 rounded-xl">
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Users className="h-4 w-4 text-blue-600" />
                        </div>
                        <p className="text-sm font-semibold text-gray-900 mt-1">
                          {plan.maxPatients === -1 ? '∞' : plan.maxPatients?.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500">patients</p>
                      </div>
                      <div className="text-center border-x border-gray-200">
                        <div className="flex items-center justify-center gap-1">
                          <UserCheck className="h-4 w-4 text-blue-600" />
                        </div>
                        <p className="text-sm font-semibold text-gray-900 mt-1">
                          {plan.maxStaff === -1 ? '∞' : plan.maxStaff}
                        </p>
                        <p className="text-xs text-gray-500">staff</p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <FileText className="h-4 w-4 text-blue-600" />
                        </div>
                        <p className="text-sm font-semibold text-gray-900 mt-1">{plan.storage}GB</p>
                        <p className="text-xs text-gray-500">storage</p>
                      </div>
                    </div>

                    <Link to="/register-clinic" className="block mb-6">
                      <Button 
                        className={`w-full py-6 text-lg font-semibold transition-all ${
                          plan.popular 
                            ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg shadow-blue-500/30' 
                            : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                        }`}
                      >
                        Start Free Trial
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </Link>
                    
                    {/* Features Header */}
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                        {totalFeatures} FEATURES INCLUDED
                      </p>
                    </div>
                    
                    {/* Features List */}
                    <div className="space-y-2">
                      {displayedFeatures.slice(3).map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-600 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Expand/Collapse Button */}
                    {hiddenFeatures > 0 && (
                      <button
                        onClick={() => togglePlanExpand(plan.name)}
                        className="mt-4 w-full flex items-center justify-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors py-2 hover:bg-blue-50 rounded-lg"
                      >
                        {isExpanded ? (
                          <>
                            <span>Show less</span>
                            <ChevronDown className="h-4 w-4 rotate-180 transition-transform" />
                          </>
                        ) : (
                          <>
                            <span>+{hiddenFeatures} more features</span>
                            <ChevronDown className="h-4 w-4 transition-transform" />
                          </>
                        )}
                      </button>
                    )}
                    
                    {/* Transaction Fee */}
                    {plan.transactionFee && (
                      <p className="text-xs text-gray-400 text-center mt-4 pt-4 border-t border-gray-100">
                        + {plan.transactionFee}% transaction fee
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          {/* Enterprise CTA */}
          <div className="mt-16 text-center bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 md:p-12">
            <h3 className="text-2xl font-bold text-white mb-4">Need a custom solution?</h3>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              For multi-location practices, custom integrations, or special requirements, we offer tailored enterprise solutions.
            </p>
            <Button 
              variant="outline" 
              size="lg"
              className="border-2 border-white text-white hover:bg-white hover:text-gray-900 transition-all"
              onClick={() => setIsDemoModalOpen(true)}
            >
              Contact Sales
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="py-24 bg-gradient-to-r from-blue-500 via-sky-500 to-blue-500 relative overflow-hidden animate-gradient">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOCAxOC04LjA1OSAxOC0xOC04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjEiLz48L2c+PC9zdmc+')] opacity-30" />
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to grow?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            See how AI can transform your membership revenue.
          </p>
          <Button 
            size="lg" 
            className="bg-white text-blue-600 hover:bg-gray-100 shadow-xl text-lg px-10 py-7 hover:scale-105 transition-all"
            onClick={() => setIsDemoModalOpen(true)}
          >
            Request Demo
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="bg-gray-900 text-white">
        {/* Main Footer */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 lg:gap-12">
            {/* Brand Column */}
            <div className="col-span-2 md:col-span-4 lg:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <Logo />
                <div className="flex flex-col">
                  <span className="text-xl font-bold leading-tight">MyMedMember</span>
                  <span className="text-xs text-gray-500 -mt-0.5">by Clinicax Systems</span>
                </div>
              </div>
              <p className="text-gray-400 text-sm mb-6 max-w-sm">
                AI-powered medical membership management platform. Grow revenue, reduce churn, and deliver exceptional patient experiences.
              </p>
              {/* Social Links - Only show if URLs are configured */}
              {(footerContent.twitter_url || footerContent.linkedin_url || footerContent.facebook_url) && (
                <div className="flex gap-4">
                  {footerContent.twitter_url && (
                    <a href={footerContent.twitter_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 hover:bg-blue-600 rounded-lg flex items-center justify-center transition-colors">
                      <Twitter className="h-5 w-5" />
                    </a>
                  )}
                  {footerContent.linkedin_url && (
                    <a href={footerContent.linkedin_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 hover:bg-blue-600 rounded-lg flex items-center justify-center transition-colors">
                      <Linkedin className="h-5 w-5" />
                    </a>
                  )}
                  {footerContent.facebook_url && (
                    <a href={footerContent.facebook_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 hover:bg-blue-600 rounded-lg flex items-center justify-center transition-colors">
                      <Globe className="h-5 w-5" />
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Product Column */}
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-3">
                <li><a href="#solution" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">Features</a></li>
                <li><a href="#ai-analytics" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">AI Analytics</a></li>
                <li><a href="#pricing" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">Pricing</a></li>
                <li><Link to="/register-clinic" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">Start Free Trial</Link></li>
                <li><button onClick={() => setIsDemoModalOpen(true)} className="text-gray-400 hover:text-blue-400 transition-colors text-sm">Request Demo</button></li>
              </ul>
            </div>

            {/* Company Column */}
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-3">
                <li><Link to="/about" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">About Us</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">Contact</Link></li>
                <li><Link to="/careers" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">Careers</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">Blog</Link></li>
                <li><Link to="/press" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">Press</Link></li>
              </ul>
            </div>

            {/* Legal & Support Column */}
            <div>
              <h4 className="font-semibold text-white mb-4">Legal & Support</h4>
              <ul className="space-y-3">
                <li><Link to="/privacy" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">Terms of Service</Link></li>
                <li><Link to="/hipaa-compliance" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">HIPAA Compliance</Link></li>
                <li><Link to="/security" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">Security</Link></li>
                <li><Link to="/help" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">Help Center</Link></li>
              </ul>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="border-t border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-wrap justify-center gap-8 items-center">
              <div className="flex items-center gap-2 text-gray-400">
                <Shield className="h-5 w-5 text-green-500" />
                <span className="text-sm">HIPAA Compliant</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Lock className="h-5 w-5 text-green-500" />
                <span className="text-sm">256-bit SSL Encryption</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <CreditCard className="h-5 w-5 text-green-500" />
                <span className="text-sm">PCI DSS Compliant</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="text-sm">SOC 2 Type II</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="text-gray-400 text-sm">
                © {new Date().getFullYear()} MyMedMember™ by Clinicax Systems. All rights reserved.
              </div>
              <div className="flex items-center gap-6 text-sm">
                <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">Privacy</Link>
                <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">Terms</Link>
                <Link to="/cookies" className="text-gray-400 hover:text-white transition-colors">Cookies</Link>
                <Link to="/sitemap" className="text-gray-400 hover:text-white transition-colors">Sitemap</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <VideoModal isOpen={isVideoModalOpen} onClose={() => setIsVideoModalOpen(false)} />
      <DemoSchedulingModal isOpen={isDemoModalOpen} onClose={() => setIsDemoModalOpen(false)} />
    </div>
  );
};

export default ModernLandingPage;
